import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useId } from "react";
import {
  ASSIGN_HELPER_TEXT,
  ORGANIZATION_LABEL,
  ORGANIZATION_SEARCH_PLACEHOLDER,
  organizationFallbackLabel,
  PRICE_TIER_LABEL,
  SUGGESTED_TIER_HINT,
  TIER_PLACEHOLDER,
} from "@/constants/adminCollections";
import { PRODUCT_IMAGE_FALLBACK } from "@/constants/campaignBuilder";
import type {
  AdminCollectionProduct,
  AssignFormValue,
  OrganizationOption,
} from "@/interfaces/adminCollections";
import { formatUsd } from "@/utils/adminCollections-helper";

interface AssignOrganizationFormProps {
  product: AdminCollectionProduct;
  organizationOptions: readonly OrganizationOption[];
  priceTiers: readonly number[];
  value: AssignFormValue;
  onChange: (next: AssignFormValue) => void;
  error?: string | null;
  /** Label for the current organization when it is not among `organizationOptions`. */
  currentOrganizationName?: string;
}

const LABEL_CLS =
  "mb-1.5 block font-bold font-lato text-[11px] text-graphite uppercase tracking-[1.5px]";

/** Presentational form body of the assign modal: the caller owns state, validation and the API. */
export default function AssignOrganizationForm({
  product,
  organizationOptions,
  priceTiers,
  value,
  onChange,
  error,
  currentOrganizationName,
}: AssignOrganizationFormProps) {
  const tierId = useId();
  const errorId = useId();
  const listed = organizationOptions.find((o) => o.value === value.organizationId);
  // An org that is set but absent from the options (e.g. the list is still loading) must never
  // read as "Unassigned": synthesize its option from the known name.
  const synthesized: OrganizationOption | null =
    !listed && value.organizationId !== null
      ? {
          value: value.organizationId,
          label: currentOrganizationName ?? organizationFallbackLabel(value.organizationId),
        }
      : null;
  const selected = listed ?? synthesized ?? organizationOptions[0] ?? null;
  const allOptions = synthesized ? [...organizationOptions, synthesized] : [...organizationOptions];
  const tierDisabled = value.organizationId === null;
  const showSuggestedHint =
    product.assignment === null &&
    product.suggestedPriceTier !== null &&
    value.priceTier === product.suggestedPriceTier;

  return (
    <div>
      <div className="mb-5 flex items-center gap-3 rounded-lg border border-smoke p-3">
        <img
          src={product.imageUrl || PRODUCT_IMAGE_FALLBACK}
          alt=""
          onError={(event) => {
            const img = event.currentTarget;
            if (img.src !== PRODUCT_IMAGE_FALLBACK) img.src = PRODUCT_IMAGE_FALLBACK;
          }}
          className="h-14 w-14 rounded object-cover"
        />
        <div className="min-w-0">
          <div className="font-bold font-lato text-[10px] text-dark-dust uppercase tracking-[1.5px]">
            {product.category}
          </div>
          <div className="font-bold font-lato text-rodeo-black text-sm">{product.name}</div>
        </div>
      </div>

      <span className={LABEL_CLS}>{ORGANIZATION_LABEL}</span>
      <Autocomplete<OrganizationOption, false, false>
        options={allOptions}
        value={selected}
        size="small"
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        onChange={(_, option) => {
          const organizationId = option?.value ?? null;
          // Picking "Unassigned" clears the tier; refill the suggestion when an org comes back.
          const refill =
            value.organizationId === null && organizationId !== null && value.priceTier === null;
          onChange({
            organizationId,
            priceTier:
              organizationId === null
                ? null
                : refill
                  ? product.suggestedPriceTier
                  : value.priceTier,
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={ORGANIZATION_SEARCH_PLACEHOLDER}
            inputProps={{ ...params.inputProps, "aria-label": ORGANIZATION_LABEL }}
          />
        )}
      />

      <label htmlFor={tierId} className={`${LABEL_CLS} mt-4`}>
        {PRICE_TIER_LABEL}
      </label>
      <select
        id={tierId}
        value={value.priceTier === null ? "" : String(value.priceTier)}
        disabled={tierDisabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        onChange={(e) =>
          onChange({
            ...value,
            priceTier: e.target.value === "" ? null : Number(e.target.value),
          })
        }
        className={`w-full rounded-lg border border-smoke bg-white px-3.5 py-2.5 font-lato text-rodeo-black text-sm focus:border-dust-ink focus:outline-none ${tierDisabled ? "opacity-40" : ""}`}
      >
        <option value="">{TIER_PLACEHOLDER}</option>
        {priceTiers.map((tier) => (
          <option key={tier} value={String(tier)}>
            {formatUsd(tier)}
          </option>
        ))}
      </select>

      {showSuggestedHint && (
        <p className="mt-1.5 font-lato text-graphite text-xs">{SUGGESTED_TIER_HINT}</p>
      )}
      <p className="mt-3 font-lato text-graphite text-xs">{ASSIGN_HELPER_TEXT}</p>
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 font-lato text-good-wine text-xs">
          {error}
        </p>
      )}
    </div>
  );
}
